import React, { useState } from 'react';
import { Search, X, Loader2, CreditCard, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { giftcardApi } from '../../../services/giftcardApi';

export default function GiftCardBalanceCheckModal({ open, onClose }) {
  const [code, setCode] = useState('');
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleCheck = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    setCard(null);
    try {
      const res = await giftcardApi.checkBalance({
        code: code.trim(),
      });
      setCard(res);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const reset = () => { setCode(''); setCard(null); setError(''); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="gc-balance-modal">
      <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-sm border border-white/10">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-cyan-400" />
            <span className="text-white font-semibold">Check Balance</span>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="text-gray-500 hover:text-white" data-testid="gc-balance-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex gap-2">
            <input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="GC-XXXX-XXXX-XXXX"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleCheck()}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono placeholder-gray-600 outline-none focus:border-cyan-400/50"
              data-testid="gc-balance-code-input"
            />
            <Button onClick={handleCheck} disabled={loading || !code.trim()}
              className="bg-cyan-500 hover:bg-cyan-600 px-4" data-testid="gc-balance-check-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            </Button>
          </div>

          {card && (
            <div className="bg-white/5 rounded-xl p-4 space-y-3" data-testid="gc-balance-result">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Current Balance</span>
                <span className={`text-3xl font-bold ${card.current_balance > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  ${card.current_balance?.toFixed(2)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Original</p>
                  <p className="text-white">${card.original_amount?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Status</p>
                  <p className="text-white capitalize">{card.status?.replace('_', ' ')}</p>
                </div>
                {card.recipient_name && (
                  <div className="col-span-2">
                    <p className="text-gray-500 text-xs">Recipient</p>
                    <p className="text-white">{card.recipient_name}</p>
                  </div>
                )}
                {card.is_expired && (
                  <p className="col-span-2 text-red-400 text-xs">This card has expired</p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="gc-balance-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {card && (
            <Button onClick={reset} variant="outline" className="w-full border-white/10 text-gray-300 hover:bg-white/5">
              Check Another
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
